import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { ArrowRight, ArrowDown } from "lucide-react";
import { openCalendly } from "@/lib/calendly";
import { useT } from "@/lib/useT";

// Detect low-power devices once at module load. We use this to opt out of
// scroll-linked parallax and the rotating-word AnimatePresence cycle on
// touch devices — these were causing the homepage to "hang" on mobile.
import { IS_MOBILE } from "@/lib/isMobile";

const Hero = () => {
  const { t, lang } = useT();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  // On desktop we run the parallax / fade-out scroll-linked transforms.
  // On mobile we leave the static values (1 = no transform) so the
  // compositor doesn't have to repaint the hero image on every scroll frame.
  const opacity = useTransform(scrollYProgress, [0, 0.5], IS_MOBILE ? [1, 1] : [1, 0]);
  const scale   = useTransform(scrollYProgress, [0, 1],   IS_MOBILE ? [1, 1] : [1, 1.15]);

  const words = t("hero.words") || ["Assets", "Equity", "Wealth"];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  useEffect(() => {
    if (IS_MOBILE) return; // skip the 3s setInterval re-render loop on phones
    const interval = setInterval(() => {
      setCurrentWordIndex((prev) => (prev + 1) % words.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [words.length]);

  const stats = [
    { value: t("hero.stat1Title"), label: t("hero.stat1Label") },
    { value: t("hero.stat2Title"), label: t("hero.stat2Label") },
    { value: t("hero.stat3Title"), label: t("hero.stat3Label") },
  ];

  return (
    <section
      ref={containerRef}
      className="relative h-screen w-full overflow-hidden"
      data-testid="hero-section"
    >
      {/* Full-bleed background with parallax — self-hosted responsive WebP (LCP-critical).
          Preload for this image is in public/index.html so it starts fetching
          before JS parses. */}
      <motion.div className="absolute inset-0" style={{ scale }}>
        <img
          src="/assets/homepage/hero-berlin-1440w.webp"
          srcSet={[
            "/assets/homepage/hero-berlin-480w.webp 480w",
            "/assets/homepage/hero-berlin-960w.webp 960w",
            "/assets/homepage/hero-berlin-1440w.webp 1440w",
            "/assets/homepage/hero-berlin-1920w.webp 1920w",
          ].join(", ")}
          sizes="100vw"
          alt="Berlin skyline at night"
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
      </motion.div>

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />

      {/* Subtle ambient glow */}
      <div className="absolute bottom-0 left-0 w-[600px] h-[400px] bg-ire-accent/[0.04] rounded-full blur-[180px]" />

      {/* Main content */}
      <motion.div
        className="relative z-10 h-full flex items-center"
        style={{ opacity }}
      >
        <div className="w-full max-w-[1800px] mx-auto px-6 sm:px-8 md:px-16 lg:px-24">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left content */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="flex items-center gap-4 mb-8">
                  <motion.div
                    className="h-px bg-ire-accent"
                    initial={{ width: 0 }}
                    animate={{ width: 64 }}
                    transition={{ duration: 1, delay: 0.5 }}
                  />
                  <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">
                    {t("hero.eyebrow")}
                  </span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.4 }}
                className="font-heading text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-extralight text-white leading-[1.05] tracking-tight mb-6 sm:mb-8"
              >
                <span className="block">{t("hero.h1_line1")}</span>
                <span className="block">{t("hero.h1_line2")}</span>
                <span className="block relative h-[1.2em] overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={currentWordIndex}
                      initial={{ y: 80, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -80, opacity: 0 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="absolute text-ire-accent italic"
                    >
                      {words[currentWordIndex]}
                    </motion.span>
                  </AnimatePresence>
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="font-body text-base sm:text-lg text-white/50 leading-relaxed max-w-xl mb-4"
              >
                {t("hero.paragraph")}
              </motion.p>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.85 }}
                className="font-body text-sm text-white/25 italic mb-8 sm:mb-12 max-w-lg"
              >
                {t("hero.microLine")}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <button
                  onClick={openCalendly}
                  className="group relative inline-flex items-center justify-center gap-3 sm:gap-4 bg-ire-accent text-black px-6 sm:px-8 py-4 sm:py-5 font-body text-[10px] sm:text-xs tracking-[0.2em] uppercase font-semibold overflow-hidden"
                  data-testid="hero-cta-primary"
                >
                  <span className="relative z-10">{t("hero.ctaPrimary")}</span>
                  <ArrowRight className="w-4 h-4 relative z-10 group-hover:translate-x-2 transition-transform" />
                  <div className="absolute inset-0 bg-white translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-300" />
                </button>
                <a
                  href="#calculator"
                  className="inline-flex items-center justify-center gap-3 sm:gap-4 border border-white/20 text-white px-6 sm:px-8 py-4 sm:py-5 font-body text-[10px] sm:text-xs tracking-[0.2em] uppercase font-semibold hover:border-ire-accent hover:text-ire-accent transition-all duration-500"
                  data-testid="hero-cta-secondary"
                >
                  {t("hero.ctaSecondary")}
                </a>
              </motion.div>
            </div>

            {/* Right side — clean stat indicators */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 1.1 }}
              className="lg:col-span-5 flex flex-row lg:flex-col gap-2 lg:gap-5 overflow-x-auto lg:overflow-visible lg:items-end mt-4 lg:mt-0 pb-2 lg:pb-0 -mx-2 px-2 lg:mx-0 lg:px-0"
              data-testid="hero-stats"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.2 + index * 0.15 }}
                  className="flex items-center gap-3 lg:gap-6 bg-white/[0.03] backdrop-blur-md border border-white/[0.06] px-4 lg:px-8 py-3 lg:py-5 min-w-[160px] lg:min-w-0 lg:w-full lg:max-w-sm hover:border-ire-accent/20 transition-colors duration-500 shrink-0"
                  data-testid={`hero-stat-${index + 1}`}
                >
                  <p className="font-heading text-sm lg:text-base font-light text-ire-accent leading-tight whitespace-nowrap">
                    {stat.value}
                  </p>
                  <div className="h-6 lg:h-8 w-px bg-white/10" />
                  <p className="font-body text-[9px] lg:text-xs text-white/35 tracking-wide">
                    {stat.label}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Bottom scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
      >
        <motion.div
          className="flex flex-col items-center gap-3"
          animate={IS_MOBILE ? undefined : { y: [0, 8, 0] }}
          transition={IS_MOBILE ? undefined : { duration: 2, repeat: Infinity }}
        >
          <span className="font-body text-[10px] tracking-[0.3em] text-white/30 uppercase">
            {t("hero.scrollToExplore")}
          </span>
          <ArrowDown className="w-4 h-4 text-ire-accent" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
